import { Suspense } from "react";
import { useAtomValue } from "jotai";
import { Text, ServiceIcons, SkeletonText } from "@zapier/design-system";
import { Tooltip } from "@zapier/design-system-beta";
import {
  agentAtomFamily_SUSPENDS,
  AgentFetchResult,
  hasStreamingProgressStartedAtom,
} from "@zapier/canvas-state";
import { getUniqueAgentWorkflowApps } from "../../../../../../utils/getUniqueAgentWorkflowApps";
import useServiceById from "../../../../../../hooks/useServiceById";
import cleanUpAppId from "../../../../../../utils/cleanUpAppId";
import getAppIconUrl from "../../../../../../utils/getAppIconUrl";
import styles from "./AgentThumbnail.module.css";

type Props = {
  agentId: string;
};

function SuspendedAgentThumbnail({ agentId }: Props) {
  const agentResult = useAtomValue(agentAtomFamily_SUSPENDS(agentId)) as
    | AgentFetchResult
    | null
    | undefined;
  const getService = useServiceById();
  const hasStreamingProgressStarted = useAtomValue(
    hasStreamingProgressStartedAtom,
  );

  // Determine view state (treat null as generic error/unavailable)
  const isSuccess = agentResult?.type === "success";

  /* ------------------------------------------------------------
   * Success ‑ hydrate workflow app icons & instruction preview
   * ---------------------------------------------------------- */
  const agent = isSuccess ? agentResult.data : undefined;
  const hasInstructions = isSuccess
    ? Boolean(
        agent?.workflow.instructions_preview_text ||
          agent?.workflow.instructions,
      )
    : false;

  const uniqueWorkflowAppsHydrated = isSuccess
    ? getUniqueAgentWorkflowApps(agent!.workflow).map((appId) => {
        const size = 20;
        const service = getService(appId);
        const images = service?.images;
        const serviceName = service?.name ?? cleanUpAppId(appId);

        return {
          name: serviceName,
          src: getAppIconUrl(appId, images, size),
        };
      })
    : [];

  const serviceNames = uniqueWorkflowAppsHydrated
    .map((app) => app.name)
    .join(", ");

  /* ------------------------------------------------------------
   * Text content based on agent result
   * ---------------------------------------------------------- */
  // Default fallback copy for unknown / unavailable agent responses
  let nameText = "Agent Unavailable";
  let instructionsText = "Unable to load agent details";

  if (agentResult) {
    switch (agentResult.type) {
      case "success":
        nameText = agent!.name || "Untitled Agent";
        instructionsText = hasInstructions
          ? agent!.workflow.instructions_preview_text ||
            agent!.workflow.instructions
          : "This agent requires more instructions";
        break;
      case "not_found":
        nameText = "404 - Agent Not Found";
        instructionsText = "This agent could not be found";
        break;
      case "forbidden":
        nameText = "403 - Access Denied";
        instructionsText = "You don't have access to this agent";
        break;
    }
  }

  /* This way we don't see "Agent Unavailable" header with "Unable to load agent details" instructions */
  if (hasStreamingProgressStarted) {
    return <AgentThumbnailLoader />;
  }

  return (
    <div className={styles.root}>
      {isSuccess && Boolean(uniqueWorkflowAppsHydrated.length) && (
        <div className={styles.tooltipContainer}>
          <Tooltip placement="top" content={serviceNames}>
            <Tooltip.FocusableTrigger>
              <ServiceIcons
                hasBorder
                maxServices={3}
                size="small"
                services={uniqueWorkflowAppsHydrated}
              />
            </Tooltip.FocusableTrigger>
          </Tooltip>
        </div>
      )}
      <Text type="Body2Multiline" color="GrayWarm9">
        {nameText}
      </Text>
      <Text type="SmallPrint1" color="TextWeakest">
        <div className={styles.lineClamp3}>{instructionsText}</div>
      </Text>
    </div>
  );
}

function AgentThumbnailLoader() {
  return (
    <SkeletonText
      lines={3}
      size="body"
      textAlign="left"
      width="100%"
      color="GrayWarm3"
    />
  );
}

export function AgentThumbnail({ agentId }: Props) {
  return (
    <Suspense fallback={<AgentThumbnailLoader />}>
      <SuspendedAgentThumbnail agentId={agentId} />
    </Suspense>
  );
}
